type QueryValue = string | number | boolean | null | undefined;
type QueryMap = Record<string, QueryValue>;

type LocationLike =
    | string
    | {
        pathname?: string;
        search?: string;
    };

function getCurrentLocation(): { pathname: string; search: string } {
    return {
        pathname: window.location.pathname,
        search: window.location.search,
    };
}

function parseLocation(location?: LocationLike) {
    if (!location) return getCurrentLocation();

    if (typeof location === "string") {
        const url = new URL(location, window.location.origin);
        return { pathname: url.pathname, search: url.search };
    }

    return {
        pathname: location.pathname ?? window.location.pathname,
        search: location.search ?? window.location.search,
    };
}

function paramsToObj(params: URLSearchParams): QueryMap {
    const obj: QueryMap = {};
    params.forEach((v, k) => (obj[k] = v));
    return obj;
}

export function newPath(
    a?: LocationLike | QueryMap | ((prev: QueryMap) => QueryMap),
    b?: QueryMap | ((prev: QueryMap) => QueryMap)
): string {
    // ✅ if first arg is query object/function only => use current location
    const isQueryOnly =
        typeof a === "function" ||
        (!!a && typeof a === "object" && !("pathname" in a) && !("search" in a));

    const location = isQueryOnly ? undefined : (a as LocationLike | undefined);
    const updater = (isQueryOnly ? a : b) as QueryMap | ((prev: QueryMap) => QueryMap);

    const { pathname, search } = parseLocation(location);
    const params = new URLSearchParams(search);

    const prev = paramsToObj(params);

    const next =
        typeof updater === "function"
            ? updater(prev)
            : { ...prev, ...(updater ?? {}) };

    const nextParams = new URLSearchParams();
    for (const [k, v] of Object.entries(next)) {
        if (v === undefined || v === null || v === "") continue;
        nextParams.set(k, String(v));
    }

    const qs = nextParams.toString();
    return qs ? `${pathname}?${qs}` : pathname;
}